import { useCallback, useEffect, useRef, useState } from 'react';

export interface UseTabsOptions<T extends string> {
  onChange?: (tab: T) => void;
}

export interface UseTabsReturn<T extends string> {
  activeTab: T;
  setActiveTab: (tab: T) => void;
  activeTabRef: React.MutableRefObject<T>;
  handleTabChange: (tab: T | string) => void;
}

export function useTabs<T extends string>(
  initialTab: T,
  options: UseTabsOptions<T> = {}
): UseTabsReturn<T> {
  const { onChange } = options;

  const [activeTab, setActiveTabState] = useState<T>(initialTab);
  const activeTabRef = useRef<T>(initialTab);

  // Keep ref synced in case activeTab changes externally
  useEffect(() => {
    activeTabRef.current = activeTab;
  }, [activeTab]);

  const setActiveTab = useCallback((tab: T) => {
    // Update ref immediately to avoid stale reads in event callbacks
    activeTabRef.current = tab;
    setActiveTabState(tab);
    onChange?.(tab);
  }, [onChange]);

  const handleTabChange = useCallback((tab: T | string) => {
    setActiveTab(tab as T);
  }, [setActiveTab]);

  return { activeTab, setActiveTab, activeTabRef, handleTabChange };
}

export default useTabs;

